import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BetaNavbar from "./components/BetaNavbar";
import EmptyWorkspaceState from "./components/EmptyWorkspaceState";
import AddWorkspaceModal from "./components/AddWorkspaceModal";
import betaAPI from "v2/api";
import { showToast } from "utils/toast";
import Button from "components/common/Button";

const V2DefaultRedirect = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [redirectTo, setRedirectTo] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const fetchWorkspaces = async () => {
      setLoading(true);
      setLoadFailed(false);
      try {
        const response = await betaAPI.getWorkspaces();
        const workspaceList = response.data?.workspaces || [];

        // If workspaces exist, redirect to first one
        if (workspaceList.length > 0) {
          const savedWorkspaceId = localStorage.getItem("selectedWorkspace");
          const savedExists =
            savedWorkspaceId &&
            workspaceList.some(
              (w) => String(w.id) === String(savedWorkspaceId)
            );

          const targetId = savedExists ? savedWorkspaceId : workspaceList[0].id;
          setRedirectTo(`/v2/${targetId}`);
        }
      } catch (err) {
        console.error("Failed to fetch workspaces:", err);
        // Not "no workspaces": don't offer to create the first one. (A 401
        // has already sent the user to sign in.)
        setLoadFailed(true);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkspaces();
  }, [attempt]);

  const handleCreateWorkspace = async ({ name }) => {
    try {
      const response = await betaAPI.createWorkspace({ body: { name } });
      const newWorkspace = response.data;
      showToast.success(t("navigation.defaultRedirect.workspaceCreated"));
      setShowCreateModal(false);

      // Navigate to the new workspace
      setRedirectTo(`/v2/${newWorkspace.id}`);
    } catch (error) {
      console.error("Failed to create workspace:", error);
      showToast.error(t("navigation.defaultRedirect.createFailed"));
    }
  };

  // Redirect if we have a target
  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <BetaNavbar hideWorkspaceSelector />
        <main className="flex min-h-[calc(100vh-72px)] items-center justify-center">
          <div className="border-t-transparent h-8 w-8 animate-spin rounded-full border-2 border-[#E07A3A]" />
        </main>
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <BetaNavbar hideWorkspaceSelector />
        <main className="flex min-h-[calc(100vh-72px)] flex-col items-center justify-center gap-4">
          <p className="text-sm text-gray-600">
            {t("common.errors.loadFailed")}
          </p>
          <Button variant="secondary" onClick={() => setAttempt((n) => n + 1)}>
            {t("common.actions.retry")}
          </Button>
        </main>
      </div>
    );
  }

  // No workspaces - show empty state
  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <BetaNavbar hideWorkspaceSelector />
      <EmptyWorkspaceState onCreateWorkspace={() => setShowCreateModal(true)} />
      <AddWorkspaceModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateWorkspace}
      />
    </div>
  );
};

export default V2DefaultRedirect;
